import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NumberFormatBase, PatternFormat } from 'react-number-format';

import { detectCardTypeFromPartial, luhnCheck } from '@r6digital/rapidstor-types/react/entities/CreditCard';
import { CreditCardTypeId } from '@r6digital/rapidstor-types/enums/sitelink';

import { WizardValidationError } from '@/core/types/Wizard';

import Button from '@/core/components/elements/button';
import CardBrandIcon from '@/core/components/elements/card-brand-icon';
import ErrorTag from '@/core/components/elements/error-tag';

import classes from './styles.module.scss';

interface PaymentFormProps {
    onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: Record<string, any>;
    submittingPayment: boolean;
    update: (key: string, event: React.BaseSyntheticEvent) => void;
    updateExpiryDate: (values: Record<string, string | number>) => void;
    moveIn?: boolean;
    validationErrors: WizardValidationError[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function CardExpiry(props: any) {
    const format = (val: string) => {
        if (val === '') return '';
        let month = val.substring(0, 2);
        const year = val.substring(2, 4);

        if (month.length === 1 && parseInt(month[0]) > 1) {
            month = `0${month[0]}`;
        } else if (month.length === 2) {
            // set the lower and upper boundary
            if (Number(month) === 0) {
                month = '01';
            } else if (Number(month) > 12) {
                month = '12';
            }
        }

        return `${month}/${year}`;
    };

    return (
        <NumberFormatBase
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
            placeholder="Expiry (MM/YY)*"
            format={format}
            // eslint-disable-next-line react/destructuring-assignment
            value={`${props.month}/${props.year}`}
        />
    );
}

export default function PaymentForm({
    onSubmit,
    form,
    submittingPayment,
    update,
    updateExpiryDate,
    moveIn,
    validationErrors,
}: PaymentFormProps) {
    const { t } = useTranslation();
    const [cardNumberError, setCardNumberError] = useState<string | null>(null);
    const [expiryError, setExpiryError] = useState<string | null>(null);

    const formErrors = useMemo(() => {
        const inputNames: string[] = [
            'cardHolderName',
            'cardNumber',
            'cardExpiryMonth',
            'cardExpiryYear',
            'cardCsv',
        ];

        return validationErrors?.filter((error) => inputNames.includes(error.name));
    }, [validationErrors]);

    const partialCardType = useMemo(() => detectCardTypeFromPartial(form.cardNumber), [form.cardNumber]);
    const isAmex = partialCardType === CreditCardTypeId.AmericanExpress;
    const cardNumberPattern = isAmex ? '#### ###### #####' : '#### #### #### ####';
    const cvvPlaceholder = isAmex
        ? t('wizard:paymentForm:csvAmexPlaceholder')
        : t('wizard:paymentForm:csvPlaceholder');
    const cvvMaxLength = isAmex ? 4 : 3;

    const handleCardNumberBlur = () => {
        if (!form.cardNumber) {
            setCardNumberError(null);
            return;
        }
        if (!luhnCheck(form.cardNumber)) {
            setCardNumberError(t('wizard:paymentForm:errors:cardNumberLuhnInvalid'));
        } else {
            setCardNumberError(null);
        }
    };

    const handleExpiryBlur = () => {
        const month = form.cardExpiryMonth;
        const year = form.cardExpiryYear;
        if (!month || !year || year.length < 2) {
            setExpiryError(null);
            return;
        }
        const fullYear = 2000 + parseInt(year, 10);
        const firstMomentNextMonth = new Date(fullYear, parseInt(month, 10), 1);
        if (new Date() >= firstMomentNextMonth) {
            setExpiryError(t('wizard:paymentForm:errors:cardExpiryExpired'));
        } else {
            setExpiryError(null);
        }
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        let hasError = false;

        if (form.cardNumber && !luhnCheck(form.cardNumber)) {
            setCardNumberError(t('wizard:paymentForm:errors:cardNumberLuhnInvalid'));
            hasError = true;
        }

        const month = form.cardExpiryMonth;
        const year = form.cardExpiryYear;
        if (month && year && year.length >= 2) {
            const fullYear = 2000 + parseInt(year, 10);
            const firstMomentNextMonth = new Date(fullYear, parseInt(month, 10), 1);
            if (new Date() >= firstMomentNextMonth) {
                setExpiryError(t('wizard:paymentForm:errors:cardExpiryExpired'));
                hasError = true;
            }
        }

        if (hasError) {
            e.preventDefault();
            return;
        }
        onSubmit(e);
    };

    return (
        <>
            {moveIn && (
                <span className={`${classes.header_text}`}>
                    {t('wizard:paymentForm:title')}
                </span>
            )}

            {formErrors.map((error, index) => (
                <ErrorTag key={index} error={error} />
            ))}

            <form
                className={`${classes.payment_fields}`}
                onSubmit={handleSubmit}
                noValidate
            >
                <div className={`${classes.inputs}`}>
                    <div>
                        <input
                            type="text"
                            placeholder="Card Holder Name*"
                            required
                            name="cardHolderName"
                            value={form.cardHolderName}
                            disabled={submittingPayment}
                            onChange={(e) => update('cardHolderName', e)}
                        />
                    </div>

                    <div>
                        <div className={classes.card_number_wrapper}>
                            <PatternFormat
                                format={cardNumberPattern}
                                placeholder="Card Number*"
                                required
                                name="cardNumber"
                                value={form.cardNumber}
                                disabled={submittingPayment}
                                onValueChange={(values) => {
                                    update('cardNumber', { target: { value: values.value } } as React.BaseSyntheticEvent);
                                }}
                                onBlur={handleCardNumberBlur}
                            />
                            <CardBrandIcon
                                typeId={partialCardType}
                                className={classes.card_brand_icon}
                            />
                        </div>
                        {cardNumberError && (
                            <span className={classes.field_error}>{cardNumberError}</span>
                        )}
                    </div>

                    <div>
                        <CardExpiry
                            required
                            name="cardExpiry"
                            disabled={submittingPayment}
                            onValueChange={(values: Record<string, string | number>) => {
                                updateExpiryDate(values);
                            }}
                            onBlur={handleExpiryBlur}
                            month={form.cardExpiryMonth}
                            year={form.cardExpiryYear}
                        />
                        {expiryError && (
                            <span className={classes.field_error}>{expiryError}</span>
                        )}
                    </div>

                    <div>
                        <input
                            type="text"
                            placeholder={cvvPlaceholder}
                            name="cardCsv"
                            required
                            maxLength={cvvMaxLength}
                            value={form.cardCsv}
                            disabled={submittingPayment}
                            onChange={(e) => update('cardCsv', e)}
                        />
                    </div>
                </div>

                {!moveIn && (
                    <Button
                        disabled={submittingPayment}
                        disableScroll
                        type="primary"
                        size="medium"
                    >
                        {t('wizard:next')}
                    </Button>
                )}
            </form>
        </>
    );
}
